/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { usePathname } from "@i18n/next";
import type { HallowElementProps } from "@packages/ui";
import {
  numericViewportHeight,
  numericViewportWidth,
  spacing,
} from "@packages/ui";
import { useEffect, useRef, useState } from "react";

const scrollBodyStyles: Interpolation = {
  height: numericViewportHeight[100],
  overflowX: "hidden",
  overflowY: "auto",
  padding: spacing.none,
  position: "relative",
  width: numericViewportWidth[100],
};

/**
 * You're probably asking yourself: why? Why this? Why not just be normal? Well, we wanted to have a parallax
 * scrolling effect between the two cell phones on the unauthenticated homepage (/src/app/[locale]/index/[date]/page.tsx)
 * and the only way to do that cross-browser and consistently was to stop scrolling the <body> and start scrolling
 * a div that acted like the body. Except that broke Next's built-in scroll-to-top-on-nav behavior. So now we're
 * doing that ourselves here, too.
 *
 * If design ever gets tired of the two cell phone thing, we could refactor this out.
 */
export const ScrollBody = ({
  children,
  ...props
}: HallowElementProps<"div">) => {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const [, setLocalCopy] = useState<string>(pathname);

  useEffect(() => {
    const isParallelRoute = (route) => {
      return route.includes("/admin/") && route.includes("/members/");
    };

    setLocalCopy((oldValue) => {
      if (!isParallelRoute(oldValue) && !isParallelRoute(pathname)) {
        ref.current?.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }

      return pathname;
    });
  }, [pathname]);

  return (
    <div ref={ref} id={"scrollBody"} {...props} css={scrollBodyStyles}>
      {children}
    </div>
  );
};
